/* eslint-disable @typescript-eslint/ban-types */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import * as XLSX from "xlsx";
import { SubmitButton as Button } from "~/components/form/submit-button";
import { Link } from "@tanstack/react-router";
import { ArrowsUpDownIcon } from "@heroicons/react/24/outline";
import { parseDate } from "~/utils/basic";
import AllocationModal from "./allocation-modal";
import { toast } from "react-toastify";
import { EditTaskStatus } from "~/services/supervisor.services";

const columnHelper = createColumnHelper<any>();

const ReportTable = ({
  data,
  fields,
  reportname,
  refreshData,
}: {
  data: any[];
  fields: string[];
  reportname: string;
  refreshData?: Function;
}) => {
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  }>({ key: "", direction: "asc" });

  const [searchInputs, setSearchInputs] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openAllocationModal, setIsOpenAllocationModal] = useState(false);
  const selectedIdsRef = useRef<Set<any>>(new Set());
  const [selectionCount, setSelectionCount] = useState(0);
  const [refreshReport, setRefreshReport] = useState(0);
  const [waitText, setWaitText] = useState<boolean>(false);

  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const isStatusReport = reportname === "status_dashboard";

  type ActionType = "allocate" | "hold" | "received" | "release" | "logout";

  const actions: { action: ActionType; label: string }[] = [
    { action: "allocate", label: "Allocate" },
    { action: "release", label: "Release" },
    { action: "hold", label: "Hold" },
    { action: "received", label: "Received" },
    // { action: "logout", label: "Logout" },
  ];

  const ACTION_CONFIG: Record<
    ActionType,
    {
      validStatuses: string[];
      errorMsg: string;
    }
  > = {
    allocate: {
      validStatuses: ["pending", "received"],
      errorMsg: "Invalid Selection: Only Received or Pending tasks can be allocated.",
    },
    hold: {
      validStatuses: ["received"],
      errorMsg: "Invalid Selection: Only Received tasks can be put on Hold.",
    },
    received: {
      validStatuses: ["hold"],
      errorMsg: "Invalid Selection: Only Hold tasks can be marked as Received.",
    },
    release: {
      validStatuses: ["pending"],
      errorMsg: "Invalid Selection: Only Pending tasks can be marked as Released.",
    },
    logout: {
      validStatuses: ["pending"],
      errorMsg: "Invalid Selection: Only Pending tasks can be marked as logout.",
    },
  };

  // Debounced search
  const handleInputChange = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setSearchInputs((prev) => ({
      ...prev,
      [key]: value,
    }));

    selectedIdsRef.current.clear();
    setSelectionCount(0);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setCurrentPage(1);
    }, 300);
  };

  // Filtering
  const filteredData = useMemo(() => {
    return data.filter((row) =>
      Object.entries(searchInputs).every(([key, value]) => {
        if (!value) return true;
        return row[key]
          ?.toString()
          .toLowerCase()
          .includes(value.toLowerCase());
      })
    );
  }, [data, searchInputs]);

  // Normalize values for sorting
  const normalize = (val: any) => {
    const regex =
      /^(\d{2})-(\d{2})-(\d{4}) (\d{1,2}):(\d{2}):(\d{2}) (AM|PM)$/i;

    if (typeof val === "string" && regex.test(val)) {
      return parseDate(val);
    }
    return val ?? "";
  };

  const CloseBtn = (flag?: boolean) => {
    if (flag) {
      refreshData?.();
    }
    setIsOpenAllocationModal(false);
  };

  // Sorting
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = normalize(a[sortConfig.key]);
      const bValue = normalize(b[sortConfig.key]);

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);

  // Pagination
  const paginatedData = useMemo(() => {
    if (rowsPerPage === -1) return sortedData;

    const start = (currentPage - 1) * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, currentPage, rowsPerPage]);

  const requestSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Download handler
  const handleDownload = async (url: string, name: string) => {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error("Download failed");

      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = name;
      a.click();

      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error(err);
    }
  };

  // Aging helper
  const toSeconds = (time: string) => {
    const [h, m, s] = time.split(":").map(Number);
    return h * 3600 + m * 60 + s;
  };

  const getRowId = (row: any) => {
    return row._id || row["Task Id"];
  };

  const setRowHighlight = (id: any, isSelected: boolean) => {
    const row = document.querySelector(
      `tr[data-row-id="${id}"]`
    ) as HTMLElement | null;
    if (row) {
      row.style.backgroundColor = isSelected ? "#e6f4ff" : "transparent";
    }
  };

  const setCheckboxState = (id: any, checked: boolean) => {
    const checkbox = document.querySelector(
      `input[data-checkbox-id="${id}"]`
    ) as HTMLInputElement | null;
    if (checkbox) {
      checkbox.checked = checked;
    }
  };

  const handleRowSelect = (id: any) => {
    const isSelected = selectedIdsRef.current.has(id);

    if (isSelected) {
      selectedIdsRef.current.delete(id);
    } else {
      selectedIdsRef.current.add(id);
    }

    setRowHighlight(id, !isSelected);
    setCheckboxState(id, !isSelected);

    const headerCheckbox = document.querySelector(
      `input[data-header-checkbox="true"]`
    ) as HTMLInputElement | null;
    if (headerCheckbox) {
      headerCheckbox.checked =
        filteredData.length > 0 &&
        selectedIdsRef.current.size === filteredData.length;
    }

    setSelectionCount(selectedIdsRef.current.size);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      filteredData.forEach((row) => selectedIdsRef.current.add(getRowId(row)));
    } else {
      selectedIdsRef.current.clear();
    }

    filteredData.forEach((row) => {
      const id = getRowId(row);
      setRowHighlight(id, checked);
      setCheckboxState(id, checked);
    });

    setSelectionCount(selectedIdsRef.current.size);
  };

  // Action Cell Renderer
  const renderActionCell = (row: any) => {
    if (reportname === "output_files") {
      return (
        <Button
          variant={"secondary"}
          size="small"
          onClick={() => {
            const type = row["Output Type"];
            let name = row["File Name"];

            if (type === "PE-OTIL") name += ".zip";
            else if (type !== "JSON") name += ".xml";

            handleDownload(row["Download"], name);
          }}
        >
          Download
        </Button>
      );
    }

    if (reportname === "daily_report_files") {
      return (
        <Button
          variant={"secondary"}
          size="small"
          onClick={() =>
            handleDownload(row["Download"], row["File Name"])
          }
        >
          Download
        </Button>
      );
    }

    if (row["Task Id"]) {
      return (
        <Link
          to="/supervisor/worklist/view/$taskId"
          params={{ taskId: row["Task Id"] }}
          target="_blank"
        >
          <Button variant={"secondary"} size="small">View</Button>
        </Link>
      );
    }

    return (
      <Button variant={"secondary"} size="small" disabled>
        View
      </Button>
    );
  };

  // const columns = useMemo(
  //   () => {
  //     const baseColumns = fields.map((field, index) =>
  //       columnHelper.accessor(field, {
  //         header: () => (
  //           <div className="p-2">
  //             <div>
  //               {field === "View" || field === "Download" ? "Action" : field}
  //             </div>
  //             {field !== "View" && field !== "Download" && (
  //               <div className="flex gap-2 mt-1">
  //                 <input
  //                   ref={(el) => (inputRefs.current[field] = el)}
  //                   type="text"
  //                   defaultValue={searchInputs[field] || ""}
  //                   onChange={handleInputChange(field)}
  //                   className="border rounded text-sm w-[150px]"
  //                 />
  //                 <button onClick={() => requestSort(field)}>
  //                   <ArrowsUpDownIcon className="h-4 w-4" />
  //                 </button>
  //               </div>
  //             )}
  //           </div>
  //         ),
  //         cell: (props) =>
  //           index === fields.length - 1
  //             ? renderActionCell(props.row.original)
  //             : props.getValue(),
  //       })
  //     );

  //     if (!isStatusReport) return baseColumns;

  //     return [
  //       columnHelper.display({
  //         id: "select",
  //         header: () => (
  //           <input
  //             type="checkbox"
  //             data-header-checkbox="true"
  //             checked={
  //               filteredData.length > 0 &&
  //               selectionCount === filteredData.length // defaultChecked - problm in filter
  //             }
  //             onChange={(e) => handleSelectAll(e.target.checked)}
  //           />
  //         ),
  //         cell: (props) => {
  //           const row = props.row.original;
  //           const id = getRowId(row);

  //           return (
  //             <input
  //               type="checkbox"
  //               key={`${id}-${selectionCount}`}
  //               data-checkbox-id={id}
  //               defaultChecked={selectedIdsRef.current.has(id)}
  //               onChange={() => handleRowSelect(id)}
  //             />
  //           );
  //         },
  //       }),

  //       ...baseColumns,
  //     ];
  //   },
  //   [fields, sortConfig, reportname, selectionCount, filteredData]
  // );

  // its depend on only fields/sort/reportname — not on selectionCount - Imp change below
  const baseColumns = useMemo(
    () =>
      fields.map((field, index) =>
        columnHelper.accessor(field, {
          header: () => (
            <div className="p-2">
              <div>
                {field === "View" || field === "Download" ? "Action" : field}
              </div>
              {field !== "View" && field !== "Download" && (
                <div className="flex gap-2 mt-1">
                  <input
                    ref={(el) => (inputRefs.current[field] = el)}
                    type="text"
                    defaultValue={searchInputs[field] || ""}
                    onChange={handleInputChange(field)}
                    className="border rounded text-sm w-[150px]"
                  />
                  <button onClick={() => requestSort(field)}>
                    <ArrowsUpDownIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          ),
          cell: (props) =>
            index === fields.length - 1
              ? renderActionCell(props.row.original)
              : props.getValue(),
        })
      ),
    [fields, reportname] // sortConfig hatao bhi — sort sirf data pe apply hota hai, column def pe nahi
  );

  const columns = useMemo(() => {
    if (!isStatusReport) return baseColumns;

    return [
      columnHelper.display({
        id: "select",
        header: () => (
          <input
            type="checkbox"
            data-header-checkbox="true"
            checked={
              filteredData.length > 0 &&
              selectionCount === filteredData.length
            }
            onChange={(e) => handleSelectAll(e.target.checked)}
          />
        ),
        cell: (props) => {
          const row = props.row.original;
          const id = getRowId(row);
          return (
            <input
              type="checkbox"
              key={`${id}-${selectionCount}`}
              data-checkbox-id={id}
              defaultChecked={selectedIdsRef.current.has(id)}
              onChange={() => handleRowSelect(id)}
            />
          );
        },
      }),
      ...baseColumns,
    ];
  }, [baseColumns, selectionCount, filteredData]); // baseColumns stable hai → inputs re-mount nahi honge

  const table = useReactTable({
    columns,
    data: paginatedData,
    getCoreRowModel: getCoreRowModel(),
  });

  // Export
  const exportToExcel = () => {

    const selectedData = reportname !== "status_dashboard" ? sortedData : data.filter((row) =>
      selectedIdsRef.current.has(getRowId(row))
    );

    if (reportname === "status_dashboard") {
      if (selectedData.length === 0) {
        toast.error("No rows selected for export.", {
          theme: "colored",
        });
        return;
      }
    }

    const visibleCols = fields.filter((c) => c !== "View" && c !== "Download");

    const exportData = selectedData.map((row) => {
      const obj: any = {};
      visibleCols.forEach((col) => (obj[col] = row[col]));
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();

    let outputfile_name = "Output Data";
    if (reportname === "invoice_accuracy") {
      outputfile_name = "Invoice Accuracy Report";
    } else if (reportname === "header_accuracy") {
      outputfile_name = "Header Accuracy Report";
    } else if (reportname === "output_files") {
      outputfile_name = "Output Files";
    } else if (reportname === "milestones_dashboard") {
      outputfile_name = "Milestones Dashboard";
    } else if (reportname === "accuracy") {
      outputfile_name = "Accuracy Report";
    } else if (reportname === "accessorial_accuracy") {
      outputfile_name = "Accessorials Accuracy Report";
    } else if (reportname === "reference_accuracy") {
      outputfile_name = "Reference Accuracy Report";
    } else if (reportname === "status_dashboard") {
      outputfile_name = "Status Dashboard";
    } else if (reportname === "daily_report_files") {
      outputfile_name = "Daily Report Files";
    }

    XLSX.utils.book_append_sheet(wb, ws, outputfile_name);
    XLSX.writeFile(wb, `${outputfile_name}.xlsx`);
  };

  const handleAssign = (action: ActionType) => {
    const selectedTasks = filteredData.filter((row) =>
      selectedIdsRef.current.has(getRowId(row))
    );

    if (selectedTasks.length === 0) {
      toast.error("Please select at least one task.");
      return;
    }

    const { validStatuses, errorMsg } = ACTION_CONFIG[action];

    const isValid = selectedTasks.every((row) =>
      validStatuses.includes(row.Status?.toLowerCase() ?? "")
    );

    if (!isValid) {
      toast.error(errorMsg, { theme: "colored" });
      return;
    }

    if (action === "allocate") {
      setIsOpenAllocationModal(true);
      return;
    }

    if (action === "logout") {
      if (!window.confirm("Are you sure you want to logout User from the selected tasks?")) {
        return;
      }
    }

    // hold & received
    const transformedData = selectedTasks.map((task) => ({ taskid: task["Task Id"] }));
    setWaitText(true)
    EditTaskStatus(transformedData, action)
      .then((success:any) => {
        toast.success(success?.data?.data, { theme: "colored" });
        setRefreshReport((prev) => prev + 1); // refresh report
      })
      .catch((error: any) => {
        toast.error(error?.data?.data || "Something went wrong. Please try again.", { theme: "colored" });
      }).finally(() => setWaitText(false));
  };


  useEffect(() => {
    selectedIdsRef.current.clear();
    setSelectionCount(0);
  }, [reportname]);

  useEffect(() => {
    selectedIdsRef.current.clear();
    setSelectionCount(0);
  }, [data]);

  useEffect(() => {
    if (refreshReport >= 1) {
      refreshData?.();
    }
  }, [refreshReport]);

  const totalPages =
    rowsPerPage === -1 ? 1 : Math.ceil(sortedData.length / rowsPerPage);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    } else {
      setCurrentPage(currentPage);
    }
  }, [totalPages, currentPage, rowsPerPage]);

  return (
    <div className="w-full">
      {/* Top Controls */}
      <div className="flex justify-between mb-4">
        <div>
          Rows per page:{" "}
          <select
            value={rowsPerPage}
            onChange={(e) =>
              setRowsPerPage(
                e.target.value === "-1" ? -1 : Number(e.target.value)
              )
            }
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={-1}>All</option>
          </select>
        </div>

        <div className="flex gap-2">
          {waitText && <div className="flex gap-2 items-center ">
            <p>Please wait..</p>
            <div className={`w-8 h-8 border-2 mt-1 border-blue-700 border-t-transparent rounded-full animate-spin`}></div>
          </div>}
          <button
            onClick={exportToExcel}
            className={`py-2 px-4 rounded font-semibold bg-[#ff7a29] text-white hover:bg-[#ff6415]`}
          >Export</button>

          {reportname === "status_dashboard" && (
            <>
              {actions.map(({ label, action }) => (
                <button
                  key={action}
                  className={`py-2 px-4 rounded font-semibold bg-[#ff7a29] text-white hover:bg-[#ff6415] ${selectionCount === 0 ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  onClick={() => handleAssign(action)}
                  disabled={selectionCount === 0}
                >
                  {label}
                </button>
              ))}
              {openAllocationModal && (
                <AllocationModal
                  isOpen={openAllocationModal}
                  onClose={(flag) => CloseBtn(flag)}
                  taskIds={Array.from(selectedIdsRef.current)}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-auto h-[400px]">
        <table className="min-w-full border text-center">
          <thead className="bg-gray-50">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => (
                  <th key={h.id} className="border border-neutral-300 capitalize">
                    {flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.map((row) => {
              const aging = row.original["Aging"];
              const agingSec = aging ? toSeconds(aging) : 0;

              const isFCN =
                row.original["Document Type"]?.toLowerCase() == "fcn";
              const isPOD =
                row.original["Document Type"]?.toLowerCase() == "pod";
              const isExceptionStatus =
                row.original["Status"]?.toLowerCase() == "exception";

              const rowData = row.original;
              const id = getRowId(rowData);

              return (
                <tr
                  key={row.id}
                  data-row-id={id}
                  style={{
                    backgroundColor: selectedIdsRef.current.has(id)
                      ? "#e6f4ff"
                      : "transparent",
                  }}
                  className={
                    isExceptionStatus && isFCN && agingSec >= 7200
                      ? "border-2 border-red-300 bg-red-200"
                      : isExceptionStatus && isFCN && agingSec >= 3600
                        ? "border-2 border-amber-300 bg-amber-200"
                        : isExceptionStatus && isPOD && agingSec >= 86400
                          ? "border-2 border-red-300 bg-red-200"
                          : "border border-neutral-300"
                  }
                >
                  {row.getVisibleCells().map((cell) => {
                    const id = cell.column.id;
                    const isSticky = ["View", "Download", "select"].includes(id);
                    return (
                      <td
                        key={cell.id}
                        id={id}
                        style={
                          isSticky
                            ? {
                              position: "sticky",
                              backgroundColor: "white",
                              zIndex: 5,
                              ...(id === "select"
                                ? { left: 0 }
                                : { right: 0 }),
                            }
                            : {}
                        }
                        className="border text-sm px-2 py-1"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-between mt-4">
        <span>
          Showing {paginatedData.length} of {filteredData.length} entries
        </span>

        <div className="flex gap-2 justify-center items-center">
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
            className={`py-1 px-3 border rounded ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            Previous
          </button>

          <span>
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={() => setCurrentPage((p) => p + 1)}
            disabled={currentPage === totalPages}
            className={`py-1 px-3 border rounded ${currentPage === totalPages ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportTable;